/**
 * Tilt System
 *
 * Manages device tilt input from gyroscope (mobile) and keyboard (desktop).
 * Supports both gamma (left/right tilt) and beta (forward/backward tilt).
 * Movement type and invert direction are configurable via SettingsManager.
 *
 * Phase 2: Tilt Controls (Gamma axis - side edge tilt)
 * Phase 5: Settings - Configurable movement type and invert direction
 */

import { GAME_CONSTANTS } from '../config/constants';
import { SettingsManager, MovementType } from './SettingsManager';
import { OrientationPermissionManager } from '../utils/OrientationPermissionManager';

export class TiltSystem {
  private scene: Phaser.Scene;
  private currentTiltAngle: number = 0; // Current tilt in degrees
  private targetTiltAngle: number = 0; // Target tilt (smoothed)

  // Gyroscope support
  private hasGyroscope: boolean = false;
  private gyroPermissionGranted: boolean = false;
  private calibrationOffset: number = 0; // Offset for gyroscope calibration
  private rawGyroGamma: number = 0; // Raw gamma value from gyroscope
  private orientationHandler: ((event: DeviceOrientationEvent) => void) | null = null; // Store handler for cleanup

  // Keyboard tilt simulation
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keyboardTiltSpeed: number = 2; // Degrees per frame

  // Configuration
  private readonly maxTilt: number;
  private readonly deadzone: number;
  private readonly smoothing: number;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Load constants
    const tiltConfig = GAME_CONSTANTS.TILT;
    this.maxTilt = tiltConfig.MAX_ANGLE;
    this.deadzone = tiltConfig.DEADZONE;
    this.smoothing = tiltConfig.SMOOTHING;

    // Initialize input
    this.setupKeyboardInput();
    this.setupGyroscope();
  }

  /**
   * Setup keyboard input for desktop tilt simulation
   */
  private setupKeyboardInput(): void {
    if (!this.scene.input.keyboard) return;

    // A = tilt left, D = tilt right
    this.keyA = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
  }

  /**
   * Setup gyroscope for mobile tilt controls
   */
  private async setupGyroscope(): Promise<void> {
    // Check if DeviceOrientation API is available
    if (!window.DeviceOrientationEvent) {
      // console.log('📱 DeviceOrientation not supported');
      return;
    }

    // Check if permission was already granted this session (uses sessionStorage)
    if (OrientationPermissionManager.isGrantedThisSession()) {
      console.log('📱 Using cached orientation permission from this session');
      this.hasGyroscope = true;
      this.gyroPermissionGranted = true;
      this.startListeningToGyroscope();
      return;
    }

    // Check if permission is needed (iOS 13+)
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      // iOS - permission required
      // console.log('📱 iOS: Gyroscope permission required');
      this.hasGyroscope = true;

      // Don't start listening yet - will start after permission granted in MainMenuScene
      // This avoids the gyroscope events not firing silently
    } else {
      // Android or older iOS - permission not needed
      this.hasGyroscope = true;
      this.gyroPermissionGranted = true;
      this.startListeningToGyroscope();
    }
  }

  /**
   * Request gyroscope permission (iOS)
   * Must be called from user gesture
   * Note: This is now deprecated - use OrientationPermissionManager.requestPermission() instead
   */
  public async requestGyroscopePermission(): Promise<boolean> {
    // Use the centralized permission manager
    const granted = await OrientationPermissionManager.requestPermission();

    if (granted) {
      this.gyroPermissionGranted = true;
      this.startListeningToGyroscope();
    }

    return granted;
  }

  /**
   * Start listening to device orientation events
   */
  private startListeningToGyroscope(): void {
    // Store the handler so we can remove it later (CRITICAL for preventing memory leaks)
    this.orientationHandler = (event: DeviceOrientationEvent) => {
      this.handleDeviceOrientation(event);
    };
    window.addEventListener('deviceorientation', this.orientationHandler);
  }

  /**
   * Handle device orientation event
   */
  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    // Get settings
    const movementType = SettingsManager.getMovementType();
    const invertDirection = SettingsManager.getInvertDirection();

    // Get the appropriate axis based on movement type
    let rawValue: number;
    if (movementType === MovementType.GAMMA) {
      // Gamma (Roll): left/right tilt (-90 to 90)
      // When phone is upright in portrait: gamma ≈ 0°
      // Tilt left edge forward/down, right edge back/up: gamma negative (e.g., -30°)
      // Tilt right edge forward/down, left edge back/up: gamma positive (e.g., +30°)
      if (event.gamma === null || event.gamma === undefined) return;
      rawValue = event.gamma;
      this.rawGyroGamma = rawValue;
    } else if (movementType === MovementType.BETA) {
      // Beta (Pitch): forward/backward tilt (-180 to 180)
      // When phone is upright in portrait: beta ≈ 90°
      // Tilt top edge forward/down: beta increases (>90°)
      // Tilt top edge backward/up: beta decreases (<90°)
      if (event.beta === null || event.beta === undefined) return;
      // Normalize beta to center around 0° (subtract 90° for portrait orientation)
      rawValue = event.beta - 90;
      this.rawGyroGamma = rawValue; // Store for calibration
    } else {
      // Alpha (Yaw): rotation around vertical axis (0 to 360)
      // Compass heading: 0° = North, 90° = East, 180° = South, 270° = West
      // Rotate device clockwise: alpha increases
      // Rotate device counter-clockwise: alpha decreases
      if (event.alpha === null || event.alpha === undefined) return;
      // Normalize alpha to -180 to +180 range centered around calibration point
      rawValue = event.alpha > 180 ? event.alpha - 360 : event.alpha;
      this.rawGyroGamma = rawValue; // Store for calibration
    }

    // If we're receiving valid data, permission must be granted
    if (!this.gyroPermissionGranted) {
      // console.log('✅ Gyroscope data received - permission granted');
      this.gyroPermissionGranted = true;
    }

    // Apply calibration offset
    let tilt = rawValue - this.calibrationOffset;

    // Apply invert direction
    if (invertDirection) {
      tilt = -tilt;
    }

    // Clamp to max tilt (e.g., -15 to +15 from calibrated position)
    tilt = Phaser.Math.Clamp(tilt, -this.maxTilt, this.maxTilt);

    // Apply deadzone
    if (Math.abs(tilt) < this.deadzone) {
      tilt = 0;
    }

    this.targetTiltAngle = tilt;
  }

  /**
   * Update tilt angle each frame
   */
  public update(_delta: number): void {
    // Handle keyboard input (desktop)
    if (this.keyA && this.keyD) {
      const invertDirection = SettingsManager.getInvertDirection();
      const tiltDirection = invertDirection ? -1 : 1;

      if (this.keyA.isDown) {
        this.targetTiltAngle -= this.keyboardTiltSpeed * tiltDirection;
      }
      if (this.keyD.isDown) {
        this.targetTiltAngle += this.keyboardTiltSpeed * tiltDirection;
      }

      // Clamp keyboard tilt
      this.targetTiltAngle = Phaser.Math.Clamp(this.targetTiltAngle, -this.maxTilt, this.maxTilt);
    }

    // Smooth transition to target angle
    this.currentTiltAngle = Phaser.Math.Linear(
      this.currentTiltAngle,
      this.targetTiltAngle,
      this.smoothing
    );
  }

  /**
   * Get current tilt angle in degrees
   */
  public getTiltAngle(): number {
    return this.currentTiltAngle;
  }

  /**
   * Get current tilt angle in radians (for physics)
   */
  public getTiltRadians(): number {
    return Phaser.Math.DegToRad(this.currentTiltAngle);
  }

  /**
   * Reset tilt to neutral position
   */
  public reset(): void {
    this.currentTiltAngle = 0;
    this.targetTiltAngle = 0;
  }

  /**
   * Calibrate gyroscope to current position
   * Sets the current device orientation as the "neutral" position
   */
  public calibrate(): void {
    // Store current gamma as the calibration offset
    this.calibrationOffset = this.rawGyroGamma;
    // Reset angles to zero
    this.currentTiltAngle = 0;
    this.targetTiltAngle = 0;
    // console.log(`📱 Gyroscope calibrated at ${this.calibrationOffset.toFixed(1)}°`);
  }

  /**
   * Check if using gyroscope or keyboard
   */
  public isUsingGyroscope(): boolean {
    return this.hasGyroscope && this.gyroPermissionGranted;
  }

  /**
   * Clean up event listeners
   */
  public destroy(): void {
    // CRITICAL: Remove gyroscope listener to prevent memory leaks
    if (this.orientationHandler) {
      window.removeEventListener('deviceorientation', this.orientationHandler);
      this.orientationHandler = null;
      // console.log('🧹 TiltSystem: Removed deviceorientation listener');
    }
  }
}
